import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  BarChart3, TrendingUp, MousePointer, ShoppingCart, Eye,
  RefreshCw, Download, Calendar, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/lib/constants";

const ABDashboardPage = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ab-test/results`);
      setResults(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching A/B results:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const variants = [
    { key: "control", name: "Control", prices: { starter: 500, growth: 1000, enterprise: 1500 }, color: "#6366F1" },
    { key: "variant_a", name: "Lower Prices", prices: { starter: 399, growth: 799, enterprise: 1299 }, color: "#00E676" },
    { key: "variant_b", name: "Premium", prices: { starter: 597, growth: 1197, enterprise: 1797 }, color: "#F59E0B" },
    { key: "variant_c", name: "Starter Focus", prices: { starter: 297, growth: 997, enterprise: 1497 }, color: "#EC4899" }
  ];

  const getEventCount = (variantKey, eventType) => {
    if (!results || !results[variantKey]) return 0;
    const events = results[variantKey].events || {};
    return events[eventType] || 0;
  };

  const calculateConversionRate = (variantKey) => {
    const views = getEventCount(variantKey, "view");
    const clicks = getEventCount(variantKey, "click");
    if (views === 0) return 0;
    return ((clicks / views) * 100).toFixed(1);
  };

  const calculatePurchaseRate = (variantKey) => {
    const clicks = getEventCount(variantKey, "click");
    const purchases = getEventCount(variantKey, "purchase");
    if (clicks === 0) return 0;
    return ((purchases / clicks) * 100).toFixed(1);
  };

  const getTotalViews = () => variants.reduce((sum, v) => sum + getEventCount(v.key, "view"), 0);
  const getTotalClicks = () => variants.reduce((sum, v) => sum + getEventCount(v.key, "click"), 0);
  const getTotalPurchases = () => variants.reduce((sum, v) => sum + getEventCount(v.key, "purchase"), 0);

  const getBestVariant = () => {
    let best = { key: null, rate: 0 };
    variants.forEach(v => {
      const rate = parseFloat(calculateConversionRate(v.key));
      if (rate > best.rate) {
        best = { key: v.key, name: v.name, rate };
      }
    });
    return best;
  };

  return (
    <div className="min-h-screen pt-32 pb-20 bg-black" data-testid="ab-dashboard">
      <div className="container-custom">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">A/B Test Dashboard</h1>
            <p className="text-gray-400">
              Monitor pricing page performance across variants
            </p>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-sm text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button 
              onClick={fetchResults} 
              className="btn-outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryCard 
            icon={Eye} 
            label="Total Views" 
            value={getTotalViews()} 
            color="#6366F1" 
          />
          <SummaryCard 
            icon={MousePointer} 
            label="Total Clicks" 
            value={getTotalClicks()} 
            color="#00E676" 
          />
          <SummaryCard 
            icon={ShoppingCart} 
            label="Total Purchases" 
            value={getTotalPurchases()} 
            color="#F59E0B" 
          />
          <SummaryCard 
            icon={Target} 
            label="Best Performer" 
            value={getBestVariant().name || "N/A"} 
            subvalue={getBestVariant().rate ? `${getBestVariant().rate}% CTR` : ""}
            color="#EC4899" 
          />
        </div>

        {/* Variant Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {variants.map((variant) => (
            <VariantCard 
              key={variant.key}
              variant={variant}
              views={getEventCount(variant.key, "view")}
              clicks={getEventCount(variant.key, "click")}
              purchases={getEventCount(variant.key, "purchase")}
              conversionRate={calculateConversionRate(variant.key)}
              purchaseRate={calculatePurchaseRate(variant.key)}
            />
          ))}
        </div>

        {/* Conversion Comparison */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#00E676]" />
              Conversion Rate Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {variants.map((variant) => {
                const rate = parseFloat(calculateConversionRate(variant.key));
                const maxRate = Math.max(...variants.map(v => parseFloat(calculateConversionRate(v.key))), 1);
                const width = (rate / maxRate) * 100;
                
                return (
                  <div key={variant.key} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{variant.name}</span>
                      <span className="text-white font-medium">{rate}%</span>
                    </div>
                    <div className="h-8 bg-white/5 rounded-lg overflow-hidden">
                      <div 
                        className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-3"
                        style={{ 
                          width: `${Math.max(width, 5)}%`, 
                          backgroundColor: variant.color 
                        }}
                      >
                        {width > 20 && (
                          <span className="text-xs font-medium text-black">
                            {getEventCount(variant.key, "click")} clicks
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Pricing Breakdown */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">Pricing by Variant</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-gray-400">Variant</th>
                  <th className="text-center p-4 text-gray-400">Starter</th>
                  <th className="text-center p-4 text-gray-400">Growth</th>
                  <th className="text-center p-4 text-gray-400">Enterprise</th>
                  <th className="text-center p-4 text-gray-400">Views</th>
                  <th className="text-center p-4 text-gray-400">Clicks</th>
                  <th className="text-center p-4 text-gray-400">CTR</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => (
                  <tr key={variant.key} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: variant.color }}
                        />
                        <span className="font-medium">{variant.name}</span>
                      </div>
                    </td>
                    <td className="text-center p-4">${variant.prices.starter}</td>
                    <td className="text-center p-4">${variant.prices.growth}</td>
                    <td className="text-center p-4">${variant.prices.enterprise}</td>
                    <td className="text-center p-4 text-gray-400">
                      {getEventCount(variant.key, "view")}
                    </td>
                    <td className="text-center p-4 text-gray-400">
                      {getEventCount(variant.key, "click")}
                    </td>
                    <td className="text-center p-4">
                      <span 
                        className="px-2 py-1 rounded text-sm font-medium"
                        style={{ 
                          backgroundColor: `${variant.color}20`,
                          color: variant.color
                        }}
                      >
                        {calculateConversionRate(variant.key)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Insights */}
        <Card className="bg-[#0A0A0A] border-white/10 mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00E676]" />
              Insights & Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {getTotalViews() === 0 ? (
              <p className="text-gray-400">
                No data yet. Pricing page views will be tracked automatically when visitors view the AEO/GEO page.
              </p>
            ) : (
              <>
                {getBestVariant().key && (
                  <div className="p-4 rounded-lg bg-[#00E676]/10 border border-[#00E676]/30">
                    <p className="text-[#00E676] font-medium">
                      🏆 Best Performing Variant: {getBestVariant().name}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      With a {getBestVariant().rate}% click-through rate, this variant is outperforming others.
                    </p>
                  </div>
                )}
                <div className="p-4 rounded-lg bg-white/5">
                  <p className="text-white font-medium mb-2">Recommendations:</p>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Continue testing until you have at least 100 views per variant</li>
                    <li>• Monitor purchase conversion rates before making final decisions</li>
                    <li>• Consider running tests for 2-4 weeks for statistical significance</li>
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, subvalue, color }) => (
  <Card className="bg-[#0A0A0A] border-white/10">
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subvalue && <p className="text-xs text-gray-500">{subvalue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const VariantCard = ({ variant, views, clicks, purchases, conversionRate, purchaseRate }) => (
  <Card className="bg-[#0A0A0A] border-white/10 hover:border-white/20 transition-all">
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="w-4 h-4 rounded-full" 
            style={{ backgroundColor: variant.color }}
          />
          <CardTitle className="text-xl">{variant.name}</CardTitle>
        </div>
        <span 
          className="text-2xl font-bold"
          style={{ color: variant.color }}
        >
          {conversionRate}%
        </span>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-3 rounded-lg bg-white/5">
          <p className="text-xs text-gray-500 mb-1">Starter</p>
          <p className="font-bold">${variant.prices.starter}</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-white/5">
          <p className="text-xs text-gray-500 mb-1">Growth</p>
          <p className="font-bold">${variant.prices.growth}</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-white/5">
          <p className="text-xs text-gray-500 mb-1">Enterprise</p>
          <p className="font-bold">${variant.prices.enterprise}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-white">{views}</p>
          <p className="text-xs text-gray-500">Views</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{clicks}</p>
          <p className="text-xs text-gray-500">Clicks</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{purchases}</p>
          <p className="text-xs text-gray-500">Purchases</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default ABDashboardPage;
